import React from 'react';
import { Box, Flex, Text, Icon, Avatar, Button, useDisclosure, Drawer, DrawerOverlay, DrawerContent, DrawerBody, useBreakpointValue } from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';

// Simple icon components using SVG for a clean look
const HomeIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
  </Icon>
);

const ImagesIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M19,19H5V5H19M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M13.96,12.29L11.21,15.83L9.25,13.47L6.5,17H17.5L13.96,12.29Z" />
  </Icon>
);

const TemplatesIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20M4,6V18H20V6H4M6,9H18V11H6V9M6,13H16V15H6V13Z" />
  </Icon>
);

const EditorIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
  </Icon>
);

const SettingsIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
  </Icon>
);

const MenuIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" />
  </Icon>
);

const LogoutIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z" />
  </Icon>
);

// Navigation items configuration
const navItems = [
  { name: 'Dashboard', icon: HomeIcon, path: '/dashboard' },
  { name: 'My Images', icon: ImagesIcon, path: '/dashboard/images' },
  { name: 'Templates', icon: TemplatesIcon, path: '/templates' },
  { name: 'Editor', icon: EditorIcon, path: '/editor' },
  { name: 'Settings', icon: SettingsIcon, path: '/settings' },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <Flex h="100vh" bg="bg.primary">
      {/* Sidebar - desktop */}
      {!isMobile && (
        <Box
          w="240px"
          bg="white"
          borderRight="1px solid"
          borderColor="neutral.300"
          p={6}
          display={{ base: 'none', md: 'block' }}
          position="fixed"
          h="100vh"
          overflowY="auto"
        >
          <Box mb={10}>
            <Text fontWeight="600" fontSize="xl" letterSpacing="-0.01em" color="text.primary">
              PlayMaker Pics
            </Text>
          </Box>
          
          <Flex direction="column" gap={2}>
            {navItems.map((item) => (
              <Link to={item.path} key={item.name}>
                <Flex
                  py={2}
                  px={3}
                  alignItems="center"
                  borderRadius="md"
                  color={location.pathname === item.path ? "brand.500" : "text.primary"}
                  bg={location.pathname === item.path ? "neutral.200" : "transparent"}
                  _hover={{ bg: "neutral.200" }}
                  fontWeight={location.pathname === item.path ? "500" : "400"}
                  transition="all 0.2s"
                  borderLeft={location.pathname === item.path ? "3px solid" : "none"}
                  borderLeftColor="brand.500"
                >
                  <item.icon mr={3} boxSize={5} opacity={0.8} />
                  <Text>{item.name}</Text>
                </Flex>
              </Link>
            ))}
          </Flex>
          
          <Box mt="auto" pt={6} borderTop="1px solid" borderColor="neutral.300" position="absolute" bottom="6" width="calc(100% - 48px)">
            <Button
              variant="ghost"
              w="full"
              justifyContent="flex-start"
              fontWeight="500"
              onClick={handleLogout}
              leftIcon={<LogoutIcon boxSize={5} />}
            >
              Logout
            </Button>
          </Box>
        </Box>
      )}

      {/* Mobile header and drawer */}
      <Box 
        position="fixed" 
        w="full" 
        zIndex="999"
        display={{ base: 'block', md: 'none' }}
      >
        <Flex
          bg="white"
          h="60px"
          px={4}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="neutral.300"
        >
          <Button variant="ghost" onClick={onOpen} leftIcon={<MenuIcon />} />
          <Text fontWeight="600">PlayMaker Pics</Text>
          <Avatar size="sm" bg="brand.500" color="white" name="User" />
        </Flex>

        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent maxW="240px">
            <DrawerBody p={0}>
              <Box p={6}>
                <Text fontWeight="600" fontSize="xl" mb={6}>PlayMaker Pics</Text>
                <Flex direction="column" gap={2}>
                  {navItems.map((item) => (
                    <Link to={item.path} key={item.name} onClick={onClose}>
                      <Flex
                        py={2}
                        px={3}
                        alignItems="center"
                        borderRadius="md"
                        color={location.pathname === item.path ? "brand.500" : "text.primary"}
                        bg={location.pathname === item.path ? "neutral.200" : "transparent"}
                        _hover={{ bg: "neutral.200" }}
                        fontWeight={location.pathname === item.path ? "500" : "400"}
                      >
                        <item.icon mr={3} boxSize={5} />
                        <Text>{item.name}</Text>
                      </Flex>
                    </Link>
                  ))}
                </Flex>
                <Box mt={10} pt={6} borderTop="1px solid" borderColor="neutral.300">
                  <Button
                    variant="ghost"
                    w="full"
                    justifyContent="flex-start"
                    fontWeight="500"
                    onClick={() => {
                      handleLogout();
                      onClose();
                    }}
                    leftIcon={<LogoutIcon boxSize={5} />}
                  >
                    Logout
                  </Button>
                </Box>
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        as="main"
        flex="1"
        ml={{ base: 0, md: '240px' }}
        pt={{ base: '60px', md: 0 }}
        transition="margin-left 0.3s"
      >
        {children}
      </Box>
    </Flex>
  );
};

export default Layout; 